/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { UseQueryResult } from '@tanstack/react-query';
import {
  queryOptions,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import type { TFunction } from 'i18next';
import { countBy, sortBy } from 'lodash';
import { useMemo } from 'react';
import type { PlotPreset, TimeSeriesService } from '@opengeoweb/shared';
import { referenceTimeUtils } from '@opengeoweb/shared';
import type { TimeSliderStartEndStep } from '@opengeoweb/store';
import type { EDRServiceInformation, LocationsResult } from './api';
import {
  fetchEdrParameter,
  getCollection,
  getCollections,
  getInstances,
  getLocations,
  getServiceInformation,
} from './api';
import type {
  EDRCollection,
  EDRInstance,
  GeoJsonWithService,
  ParameterWithData,
  PlotWithData,
  PointerLocation,
  UseGetLocationReturnTypeProperties,
} from '../../components/TimeSeries/types';
import {
  combineParametersWithData,
  extractDataFromfetchEdrParameterResponse,
  extractUsedCollections,
  getPlotsWithParameters,
} from '../../components/TimeSeries/utils';
import type {
  ParameterCollectionQueryData,
  PlotErrorsMap,
  FailedParametersMap,
} from '../../components/TimeSeries/utils';
import type { TimeseriesCollectionDetail } from '../../components/TimeSeriesSelect/utils';
import {
  getFeatureClass,
  getServiceCollectionDetails,
  getCollectionDetails,
  listSupportedInstances,
  latestInstance,
  constructTimeInterval,
} from '../edrUtils';

interface CombinedResult<T> {
  data: T | undefined;
  isFetching: boolean;
  error: Error | null | undefined;
  parameterErrors?: Record<number, string>;
}

const getInstanceByRelativeReferenceTime = (
  instances: EDRInstance[],
  relativeReferenceTime?: string,
): EDRInstance | null => {
  const offset = referenceTimeUtils.parseRelativeReferenceTimeOffset(
    relativeReferenceTime,
  );
  if (offset === null) {
    return null;
  }

  const sortedInstances = instances.toSorted((instanceA, instanceB) =>
    instanceB.id.localeCompare(instanceA.id),
  );

  return sortedInstances[offset] ?? null;
};

// Ensures every timeseries query starts with the same query key
const generateTimeSeriesQueryKeys = (queryKeys: string[]): string[] =>
  ['time-series'].concat(queryKeys);

export const timeseriesEDRQueryOptions = {
  getServiceInformationQuery: (serviceUrl: string) => {
    return queryOptions({
      queryKey: generateTimeSeriesQueryKeys([
        'get-service-information',
        serviceUrl,
      ]),
      queryFn: async (): Promise<EDRServiceInformation> =>
        getServiceInformation(serviceUrl),
      enabled: !!serviceUrl,
    });
  },
  getCollectionsQuery: (serviceUrl: string) => {
    return queryOptions({
      queryKey: generateTimeSeriesQueryKeys(['get-collections', serviceUrl]),
      queryFn: async (): Promise<EDRCollection[]> => {
        const result = await getCollections(serviceUrl);
        return result.collections;
      },
      enabled: !!serviceUrl,
    });
  },
  getCollectionDetailsQuery: (
    service: TimeSeriesService,
    collectionId: string,
  ) => {
    return queryOptions({
      queryKey: generateTimeSeriesQueryKeys([
        'get-collection-details',
        service?.url,
        collectionId,
      ]),
      queryFn: async (): Promise<TimeseriesCollectionDetail> => {
        const collection = await getCollection(service.url, collectionId);
        return getCollectionDetails(service, collection);
      },
      enabled:
        !!service &&
        !!service?.url &&
        service.url !== '' &&
        !!collectionId &&
        collectionId !== '',
    });
  },
  getInstancesQuery: (
    serviceUrl: string,
    collectionId: string,
    supportsInstances = true,
  ) => {
    return queryOptions({
      queryKey: generateTimeSeriesQueryKeys([
        'get-instances',
        serviceUrl,
        collectionId,
      ]),
      queryFn: async (): Promise<EDRInstance[]> => {
        const { instances } = await getInstances(serviceUrl, collectionId);
        return listSupportedInstances(instances);
      },
      enabled:
        supportsInstances &&
        !!serviceUrl &&
        serviceUrl !== '' &&
        !!collectionId &&
        collectionId !== '',
    });
  },
} as const;

export const useGetServiceInformation = (
  serviceUrl: string,
): UseQueryResult<EDRServiceInformation> => {
  return useQuery(
    timeseriesEDRQueryOptions.getServiceInformationQuery(serviceUrl),
  );
};

export const useGetCollections = (
  serviceUrl: string,
): UseQueryResult<EDRCollection[]> => {
  return useQuery(timeseriesEDRQueryOptions.getCollectionsQuery(serviceUrl));
};

export const useGetDetailsForCollection = (
  service: TimeSeriesService,
  collectionId: string,
): UseQueryResult<TimeseriesCollectionDetail> => {
  return useQuery(
    timeseriesEDRQueryOptions.getCollectionDetailsQuery(service, collectionId),
  );
};

export const useGetInstances = (
  serviceUrl: string,
  collectionId: string,
  supportsInstances = true,
): UseQueryResult<EDRInstance[]> => {
  return useQuery(
    timeseriesEDRQueryOptions.getInstancesQuery(
      serviceUrl,
      collectionId,
      supportsInstances,
    ),
  );
};

export const useGetServiceCollectionDetails = (
  services: TimeSeriesService[],
  isOpen: boolean,
): CombinedResult<TimeseriesCollectionDetail[]> => {
  const queryClient = useQueryClient();
  const queries = useMemo(() => {
    return services.map((service) => {
      return {
        queryKey: generateTimeSeriesQueryKeys([
          'get-service-collections-details',
          service?.id,
          service?.url,
        ]),
        queryFn: async (): Promise<TimeseriesCollectionDetail[]> => {
          const collections = await queryClient.fetchQuery(
            timeseriesEDRQueryOptions.getCollectionsQuery(service.url),
          );

          return getServiceCollectionDetails(service, collections);
        },

        enabled: isOpen && !!service?.id && !!service?.url,
        refetchInterval: 10 * 60 * 1000,
      };
    });
  }, [services, isOpen, queryClient]);

  return useQueries({
    queries,
    combine: (
      results: UseQueryResult<TimeseriesCollectionDetail[]>[],
    ): CombinedResult<TimeseriesCollectionDetail[]> => {
      const data = results
        .filter((query) => query.isSuccess && query.data)
        .flatMap((query) => query.data || []);
      const isFetching = results.some((result) => result.isFetching);
      const error = results.find((result) => result?.error)?.error;
      return { data, isFetching, error };
    },
  });
};

export const useGetCollectionsAndInstanceDetails = (
  queryData: Record<string, ParameterCollectionQueryData>,
): CombinedResult<TimeseriesCollectionDetail[]> => {
  const queryClient = useQueryClient();

  const queries = useMemo(() => {
    return Object.values(queryData).map((query) => {
      return {
        queryKey: generateTimeSeriesQueryKeys([
          'get-collection-and-instances-details',
          query.service.url,
          query.collectionId,
        ]),
        queryFn: async (): Promise<TimeseriesCollectionDetail | null> => {
          const { service, collectionId } = query;
          const collectionDetails = await queryClient.fetchQuery(
            timeseriesEDRQueryOptions.getCollectionDetailsQuery(
              service,
              collectionId,
            ),
          );

          // Only fetch instances for collections that advertise them and that support location & position queries
          if (
            !!collectionDetails.dataQueries?.instances &&
            (!!collectionDetails.dataQueries?.locations ||
              !!collectionDetails.dataQueries?.position)
          ) {
            const allInstances = await queryClient.fetchQuery(
              timeseriesEDRQueryOptions.getInstancesQuery(
                service.url,
                collectionId,
              ),
            );
            return {
              ...collectionDetails,
              allInstances,
            };
          }
          return collectionDetails;
        },
        enabled: !!query.service.url && !!query.collectionId,
        refetchInterval: 5 * 60 * 1000,
      };
    });
  }, [queryClient, queryData]);

  return useQueries({
    queries,
    combine: (
      results: UseQueryResult<TimeseriesCollectionDetail | null>[],
    ): CombinedResult<TimeseriesCollectionDetail[]> => {
      const data = results
        .filter((query) => query.isSuccess && query.data)
        .flatMap((query) => query.data || []);
      const isFetching = results.some((result) => result.isFetching);
      const error = results.find((result) => result?.error)?.error;
      return { data, isFetching, error };
    },
  });
};

export const useGetPlotsWithData = (
  plotsWithoutData: PlotPreset,
  selectedLocation: PointerLocation,
  services: TimeSeriesService[],
  splitGeoJsonByCollection: GeoJsonWithService[],
  t: TFunction,
  syncgroupTimeState?: TimeSliderStartEndStep,
): {
  data: PlotWithData[] | undefined;
  isFetching: boolean;
  error: Error | null | undefined;
} => {
  // Create array of collections we need to query for the data
  const collectionsList = useMemo(
    () => extractUsedCollections(plotsWithoutData, services),
    [plotsWithoutData, services],
  );

  // First fetch collection and any relevant instance data
  const { data: collectionDetails, isFetching: isDetailsFetching } =
    useGetCollectionsAndInstanceDetails(collectionsList);

  const geojsonMap = useMemo(
    () =>
      new Map(
        splitGeoJsonByCollection.map((gjs) => [
          `${gjs.serviceName}_${gjs.collectionName}`,
          gjs,
        ]),
      ),
    [splitGeoJsonByCollection],
  );

  // Construct all queries to be executed
  const queries = useMemo(
    () =>
      plotsWithoutData.parameters.map((parameter) => {
        // Note: service can be undefined
        const service: TimeSeriesService | undefined =
          collectionsList[`${parameter.serviceId}-${parameter.collectionId}`]
            ?.service;
        const collection = collectionDetails?.find(
          (collection) =>
            collection.serviceId === parameter.serviceId &&
            collection.collectionId === parameter.collectionId,
        );

        const instanceInfo = getInstanceByRelativeReferenceTime(
          collection?.allInstances || [],
          parameter.instanceId,
        ) ||
          (parameter.instanceId &&
            collection?.allInstances?.find(
              (inst) => inst.id === parameter.instanceId,
            )) ||
          latestInstance(collection?.allInstances || []) || { id: '' };

        // Construct timeinterval that needs to be fetched
        // Create here so if new data is advertised by the collection/instance a new request is made
        const intervalParam = constructTimeInterval(
          collection,
          instanceInfo,
          syncgroupTimeState,
        );

        return {
          queryKey: generateTimeSeriesQueryKeys([
            'get-parameter-data',
            parameter.id || 'undefined',
            parameter.plotId,
            parameter.propertyName,
            parameter.serviceId,
            parameter.collectionId,
            parameter.instanceId || 'undefined',
            parameter.dimension?.value || 'undefined',
            intervalParam,
            selectedLocation.lat.toString() || 'undefined',
            selectedLocation.lon.toString() || 'undefined',
          ]),
          queryFn: async (): Promise<ParameterWithData | null> => {
            if (!collection || !service) {
              throw new Error('error-message-missing-collection-or-service');
            }

            const edrResponse = await fetchEdrParameter(
              parameter,
              selectedLocation,
              service,
              geojsonMap,
              intervalParam,
              collection,
              instanceInfo,
            );
            return extractDataFromfetchEdrParameterResponse(
              edrResponse,
              parameter,
            );
          },
          enabled:
            !!parameter &&
            !!selectedLocation.lon &&
            !!selectedLocation.lat &&
            // This condition allows us to see errors about missing collections/services
            // that can not be explained by unfinished previous queries
            (!isDetailsFetching || (!!collection && !!service)),
          refetchInterval: 5 * 60 * 1000,
          retry: false,
        };
      }),
    [
      plotsWithoutData.parameters,
      collectionsList,
      collectionDetails,
      syncgroupTimeState,
      selectedLocation,
      isDetailsFetching,
      geojsonMap,
    ],
  );
  const results = useQueries({
    queries,
    combine: (
      results: UseQueryResult<ParameterWithData | null>[],
    ): CombinedResult<ParameterWithData[]> => {
      const data = results
        .filter((query) => query.isSuccess && query.data)
        .flatMap((query) => query.data || []);

      const isFetching = results.some((result) => result.isFetching);

      const parameterErrors: Record<number, string> = {};
      results.forEach((result, index) => {
        if (result.error?.message) {
          parameterErrors[index] = result.error.message;
        }
      });

      const errorsWithCount = countBy(
        results.map((result) => result?.error?.message).filter(Boolean),
      );
      const errorMessage = Object.entries(errorsWithCount)
        .map(([message, count]) => `"${t(message)}" (x${count})`)
        .join(', ');

      const error = errorMessage ? new Error(errorMessage) : null;
      return { data, isFetching, error, parameterErrors };
    },
  });

  // Combine fetched data with parameters and plots, including errors per plot
  const plotsWithData = useMemo(() => {
    const data = results.data?.length ? results.data : [];

    // Create maps of parameter errors and failed parameter names by plotId
    const plotErrors: PlotErrorsMap = {};
    const failedParametersByPlotId: FailedParametersMap = {};

    plotsWithoutData.parameters.forEach((parameter, index) => {
      const errorMessage = results.parameterErrors?.[index];
      if (errorMessage) {
        if (!plotErrors[parameter.plotId]) {
          plotErrors[parameter.plotId] = [];
          failedParametersByPlotId[parameter.plotId] = [];
        }
        if (!plotErrors[parameter.plotId].includes(errorMessage)) {
          plotErrors[parameter.plotId].push(errorMessage);
        }
        failedParametersByPlotId[parameter.plotId].push({
          propertyName: parameter.propertyName,
          serviceId: parameter.serviceId,
          collectionId: parameter.collectionId,
        });
      }
    });

    return getPlotsWithParameters(
      plotsWithoutData.plots,
      combineParametersWithData(plotsWithoutData.parameters, data),
      plotErrors,
      failedParametersByPlotId,
    );
  }, [
    plotsWithoutData.parameters,
    plotsWithoutData.plots,
    results.data,
    results.parameterErrors,
  ]);

  return {
    ...results,
    data: plotsWithData,
  };
};

export interface GetLocationsOption {
  service: TimeSeriesService;
  collectionId: string;
}

export const useGetLocations = (
  options: GetLocationsOption[],
): {
  data: (
    | FeatureCollection<Geometry, UseGetLocationReturnTypeProperties>
    | undefined
  )[];
  isFetching: boolean;
  error: Error | null | undefined;
} => {
  const queries = useMemo(
    () =>
      options.map((option) => ({
        queryKey: generateTimeSeriesQueryKeys([
          'get-timeseries-locations',
          option.service.id,
          option.collectionId,
        ]),
        queryFn: async (): Promise<LocationsResult> =>
          getLocations(option.service.url, option.collectionId),
      })),
    [options],
  );

  const results = useQueries({
    queries,
    combine: (results) => {
      const resultList = results.map((result, index) => {
        if (result.isPending || result.isError) {
          return undefined;
        }
        const [, , serviceId, collectionId] = queries[index].queryKey;
        const features: Feature<
          Geometry,
          UseGetLocationReturnTypeProperties
        >[] = result?.data?.features.map((feature) => {
          const { geometry } = feature;
          const featureClass = getFeatureClass(geometry.type);

          return {
            id: feature.id,
            type: 'Feature',
            properties: {
              name:
                feature.properties.name ||
                feature.properties.NAME ||
                feature.properties.Name,
              detail: feature.properties.detail,
              serviceId,
              collectionId,
              parameterName: feature.properties['parameter-name'],
              mapFeatureClass: featureClass,
            },
            geometry,
          };
        });
        return {
          type: 'FeatureCollection' as const,
          features: sortBy(features, (feature) => feature.properties?.name),
        };
      });
      const data = resultList.filter((result) => result !== undefined);
      const isFetching = results.some((result) => result.isFetching);
      const error = results.find((result) => result?.error)?.error;
      return { data, isFetching, error };
    },
  });
  return results;
};
